import { AppStateSlice } from '$tablet-app/src/app/stores/app_state';
import { MealType } from '@akin-home/amplify';
import { MealPlanPreferenceStateVariableNames as PreferenceVariables } from '@akin-home/meals';
import {
  AllergyText,
  MealAvoidance,
  MealPlanPreferences,
  MeatType,
} from '@akin-home/recipes';
import {
  StateVariable,
  stateVariableFunctions,
} from '@akin-home/state-variables';
import { getStateVariables } from '@akin-home/state-variables-frontend';
import { getUserChatID } from '@akin-home/user';
import { getUserChatAppID } from '../../functions';
import { UserStateStore } from './types';

export const createUserStateStore: AppStateSlice<UserStateStore> = (
  set,
  get
) => ({
  async getUserMealPreferences() {
    const user = get().current_user;

    const preferences: MealPlanPreferences = {};

    if (user) {
      const state_variables =
        (await getStateVariables({
          username: getUserChatID(user),
          application_id: getUserChatAppID(user),
          names: Object.values(PreferenceVariables),
        })) || [];

      const { findNumberVariable, findBooleanVariable } =
        stateVariableFunctions(state_variables);

      preferences.budget = findNumberVariable(
        PreferenceVariables.BUDGET
      )?.value;

      preferences.people = findNumberVariable(
        PreferenceVariables.PEOPLE
      )?.value;

      preferences.time_and_effort = findNumberVariable(
        PreferenceVariables.DIFFICULTY
      )?.value;

      preferences.dietary_balance = findNumberVariable('Meat_type_diet')?.value;

      preferences.avoidances = (user?.avoidances || undefined) as
        | MealAvoidance[]
        | undefined;

      preferences.eats_meat = findBooleanVariable(
        PreferenceVariables.EATS_MEAT
      )?.value;

      preferences.meats_eaten = getMeatsEaten(state_variables);
      preferences.meals_to_plan = getMealsToPlan(state_variables);
      preferences.reminders = getMealReminders(state_variables);
      preferences.meal_times = getMealTimes(state_variables);

      /** Fill allergies */
      preferences.allergies = getAllergies(
        state_variables,
        (user.allergies || []) as string[]
      );
    }

    return preferences;
  },

  async getStateVariables(names) {
    const user = get().current_user;

    if (!user) return;

    return await getStateVariables({
      application_id: getUserChatAppID(user),
      username: getUserChatID(user),
      names,
    });
  },
});

const getAllergies = (
  state_variables: StateVariable[],
  user_allergies: string[]
) => {
  const { findBooleanVariable } = stateVariableFunctions(state_variables);

  const allergyMap: Record<AllergyText, boolean | undefined> = {
    [AllergyText.DAIRY]:
      findBooleanVariable('User_dairy_allergy')?.value ||
      user_allergies.includes(AllergyText.DAIRY),

    [AllergyText.EGGS]:
      findBooleanVariable('User_egg_allergy')?.value ||
      user_allergies.includes(AllergyText.EGGS),

    [AllergyText.PEANUTS]:
      findBooleanVariable('User_peanut_allergy')?.value ||
      user_allergies.includes(AllergyText.PEANUTS),

    [AllergyText.SESAME]:
      findBooleanVariable('User_sesame_allergy')?.value ||
      user_allergies.includes(AllergyText.SESAME),

    [AllergyText.SHELLFISH]:
      findBooleanVariable('User_shellfish_allergy')?.value ||
      user_allergies.includes(AllergyText.SHELLFISH),

    [AllergyText.SOY]:
      findBooleanVariable('User_soy_allergy')?.value ||
      user_allergies.includes(AllergyText.SOY),

    [AllergyText.TREE_NUTS]:
      findBooleanVariable('User_treenut_allergy')?.value ||
      user_allergies.includes(AllergyText.TREE_NUTS),

    [AllergyText.WHEAT]:
      findBooleanVariable('User_wheat_allergy')?.value ||
      user_allergies.includes(AllergyText.WHEAT),
  };

  return Object.keys(allergyMap).filter(
    (key) => allergyMap[key as AllergyText]
  );
};

const getMeatsEaten = (state_variables: StateVariable[]) => {
  const { findBooleanVariable } = stateVariableFunctions(state_variables);

  const meats_eaten: Record<MeatType, boolean | undefined> = {
    beef: findBooleanVariable('User_eats_beef')?.value,
    lamb: findBooleanVariable('User_eats_lamb')?.value,
    pork: findBooleanVariable('User_eats_pork')?.value,
    chicken: findBooleanVariable('User_eats_chicken')?.value,
    turkey: findBooleanVariable('User_eats_turkey')?.value,
    fish: findBooleanVariable('User_eats_fish')?.value,
  };

  return Object.keys(meats_eaten).filter(
    (key) => meats_eaten[key as MeatType]
  ) as MeatType[];
};

const getMealsToPlan = (state_variables: StateVariable[]) => {
  const { findBooleanVariable } = stateVariableFunctions(state_variables);
  const meals_to_plan: MealType[] = [];

  if (findBooleanVariable(PreferenceVariables.PLAN_BREAKFAST)?.value) {
    meals_to_plan.push(MealType.Breakfast);
  }

  if (findBooleanVariable(PreferenceVariables.PLAN_LUNCH)?.value) {
    meals_to_plan.push(MealType.Lunch);
  }

  if (findBooleanVariable(PreferenceVariables.PLAN_DINNER)?.value) {
    meals_to_plan.push(MealType.Dinner);
  }

  if (findBooleanVariable(PreferenceVariables.PLAN_SNACK)?.value) {
    meals_to_plan.push(MealType.Snack);
  }

  return meals_to_plan;
};

const getMealReminders = (state_variables: StateVariable[]) => {
  const reminders: MealType[] = [];
  const { findBooleanVariable } = stateVariableFunctions(state_variables);

  if (findBooleanVariable(PreferenceVariables.BREAKFAST_REMINDER)?.value) {
    reminders.push(MealType.Breakfast);
  }

  if (findBooleanVariable(PreferenceVariables.LUNCH_REMINDER)?.value) {
    reminders.push(MealType.Lunch);
  }

  if (findBooleanVariable(PreferenceVariables.DINNER_REMINDER)?.value) {
    reminders.push(MealType.Dinner);
  }

  if (findBooleanVariable(PreferenceVariables.SNACK_REMINDER)?.value) {
    reminders.push(MealType.Snack);
  }

  return reminders;
};

const getMealTimes = (
  state_variables: StateVariable[]
): Record<MealType, string | undefined> => {
  const { findTextVariable } = stateVariableFunctions(state_variables);

  return {
    [MealType.Breakfast]: findTextVariable(PreferenceVariables.BREAKFAST_TIME)
      ?.value,
    [MealType.Lunch]: findTextVariable(PreferenceVariables.LUNCH_TIME)?.value,
    [MealType.Dinner]: findTextVariable(PreferenceVariables.DINNER_TIME)?.value,
    [MealType.Snack]: findTextVariable(PreferenceVariables.SNACK_TIME)?.value,
  };
};
